import {
  useFinancialCoefficientRangeServiceFinancialCoefficientRangeEffectiveRetrieve,
  useSalesServiceSalesOrdersPartialUpdate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import {
  OrderDetail,
  PriceFinancial,
  Status9e7Enum
} from '@insurance/api/requests'
import { ProductCode } from '@insurance/types'
import { DEFAULT_CURRENCY } from '@shared/constants'
import { usePermissionRestriction } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import { Card, Col, Divider, Form, Modal, Row } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CustomerStep } from './CustomerStep'
import { Details } from './Details'
import {
  FinancialInsuranceProvider,
  UpdateOrderOptions
} from './FinancialInsuranceContext'
import { FinancialInsuranceControls } from './FinancialInsuranceControls'
import { InformationStep } from './InformationStep'
import { MainStep } from './MainStep'
import { FinancialFormValues } from './type'
import {
  formProps,
  getFinancialValuesFromStorage,
  normalizeCommonData,
  normalizeEditOrderData,
  normalizeRecalculateData,
  removeFinancialValuesFromStorage,
  resizePersons,
  saveFinancialValuesToStorage
} from './utils'
import {
  iniMixtContractorValues,
  serializeContractorFields
} from '../PersonsFields'

const forms = [
  <MainStep key={0} />,
  <InformationStep key={1} />,
  <CustomerStep key={2} />
]

const {
  stepKey,
  priceKey,
  orderKey,
  coefficientMinKey,
  coefficientMaxKey,
  personsProps,
  priceDataKey,
  validFromProps,
  validToProps,
  personsCountProps,
  contractorProps,
  customerProps,
  territoryProps
} = formProps()

const initValues = {
  [stepKey.name]: 0,
  [priceKey.name]: undefined,
  [orderKey.name]: undefined,
  [validFromProps.name]: dayjs(),
  [validToProps.name]: undefined,
  [personsCountProps.name]: 1,
  [personsProps.name]: [],
  [contractorProps.name]: iniMixtContractorValues.contractor,
  [customerProps.name]: undefined,
  [territoryProps.name]: undefined,
  [priceDataKey.name]: undefined,
  same_contractor_as_first_person: true
}

type Props = {
  order?: OrderDetail
}

export const FinancialInsurance = ({ order }: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isReady, setIsReady] = useState(false)
  const { hasPermission } = usePermissionRestriction()

  // Resuming a draft: seed the form from what was stored on the order. The fields
  // stay editable — this is continuing data entry, not amending an issued policy.
  const formattedInitValues = useMemo(() => {
    const detailValues = order?.financial

    if (!detailValues) return initValues

    serializeContractorFields({ hasPermission }, detailValues)

    if (detailValues.contractor?.person?.date_of_birth) {
      detailValues.contractor.person.date_of_birth = dayjs(
        detailValues.contractor.person.date_of_birth
      ) as unknown as string
    }

    return {
      ...initValues,
      ...detailValues,
      ...(detailValues.valid_from && {
        [validFromProps.name]: dayjs(detailValues.valid_from)
      }),
      ...(detailValues.valid_to && {
        [validToProps.name]: dayjs(detailValues.valid_to)
      }),
      [stepKey.name]: order.stage || 0,
      [orderKey.name]: order.id,
      [priceKey.name]: {
        amount: order.full_amount,
        currency: DEFAULT_CURRENCY
      },
      [priceDataKey.name]: order.price_data,
      [customerProps.name]: order.customer,
      [personsCountProps.name]:
        detailValues.persons?.length || detailValues.persons_count || 1,
      [personsProps.name]: detailValues.persons?.length
        ? detailValues.persons?.map((person) => ({
            ...person,
            ...(person?.date_of_birth && {
              date_of_birth: dayjs(person.date_of_birth)
            }),
            document: [person?.document?.series, person?.document?.number]
              .filter(Boolean)
              .join('')
          }))
        : Array.from({ length: detailValues.persons_count || 1 }).map(
            () => ({})
          ),
      same_contractor_as_first_person:
        detailValues.persons?.[0]?.identification_number ===
        detailValues.contractor?.person?.identification_number
    }
  }, [order])

  const { data: financialRange } =
    useFinancialCoefficientRangeServiceFinancialCoefficientRangeEffectiveRetrieve()

  const { mutate: calculate, isPending: isCalculatingLoading } =
    useSalesServiceSalesProductsPriceCreate()

  const { mutate: updateOrder, isPending: isUpdateOrderLoading } =
    useSalesServiceSalesOrdersPartialUpdate()

  const applyPrice = (priceData?: PriceFinancial) => {
    if (!priceData) return

    form.setFieldValue(priceKey.name, {
      currency: DEFAULT_CURRENCY,
      amount: Number(Number(priceData.amount)?.toFixed(2))
    })
    form.setFieldValue(priceDataKey.name, priceData)
  }

  const currentStep = () => (form.getFieldValue(stepKey.name) as number) ?? 0

  /**
   * Quote until an order exists, patch it from then on — so the stored data and
   * the shown price never drift apart. Also the single entry point the controls
   * use, which is why it takes the stage they are heading to.
   */
  const runStep = (options?: UpdateOrderOptions) => {
    const values = form.getFieldsValue(true) as FinancialFormValues
    const orderId = form.getFieldValue(orderKey.name) as number | undefined

    if (orderId) {
      updateOrder(
        {
          id: orderId,
          requestBody: {
            stage: options?.stage ?? currentStep(),
            status: Status9e7Enum.DATA_ENTRY,
            financial: options?.withOrderData
              ? normalizeEditOrderData(values, false)
              : normalizeRecalculateData(values)
          }
        },
        {
          onSuccess: (order) => {
            applyPrice(order.price_data)
            options?.onSuccess?.()
          },
          onError: (error) =>
            showApiErrors(error, form, { unwrapKeys: ['financial'] })
        }
      )

      return
    }

    calculate(
      { code: ProductCode.FINANCIAL, requestBody: normalizeCommonData(values) },
      {
        onSuccess: (priceResponse) => {
          const priceData = priceResponse as PriceFinancial
          const draftOrderId = priceData?.order?.id

          applyPrice(priceData)

          // The quote spawned a draft — adopt it and park it in data_entry so it
          // shows up as resumable. That patch omits the `financial` key on
          // purpose: EditFinancial wants a complete contractor and persons set,
          // and neither exists at the quote step.
          if (draftOrderId) {
            form.setFieldValue(orderKey.name, draftOrderId)
            updateOrder({
              id: draftOrderId,
              requestBody: {
                status: Status9e7Enum.DATA_ENTRY,
                stage: currentStep() + 1
              }
            })
          }

          options?.onSuccess?.()
        },
        onError: (error) => showApiErrors(error, form)
      }
    )
  }

  // The quote step's own button. Contractor and persons are not on this screen,
  // so an existing order is patched with the pricing fields only — the full order
  // shape is sent later, from the step that actually collects them.
  const handleCalculate = async (onSuccess: () => void) => {
    await form.validateFields()

    // Match the persons list to the count before moving on.
    const values = form.getFieldsValue(true) as FinancialFormValues
    form.setFieldValue(
      personsProps.name,
      resizePersons(values.persons, values.persons_count)
    )

    runStep({ stage: currentStep() + 1, onSuccess })
  }

  const handleRecalculate = () => runStep()

  const saveOnChangeValue = () => {
    saveFinancialValuesToStorage(form.getFieldsValue(true), order?.id)
    return null
  }

  useEffect(() => {
    form.setFieldsValue({
      correction_coefficient: financialRange?.default,
      ...formattedInitValues,
      ...getFinancialValuesFromStorage(order?.id)
    })
  }, [form, financialRange, formattedInitValues, order?.id])

  useEffect(() => {
    const values = getFinancialValuesFromStorage(order?.id)
    if (!values) return

    const modal = Modal.confirm({
      onCancel: () => {
        removeFinancialValuesFromStorage(order?.id)
        setIsReady(false)
        form.setFieldsValue(formattedInitValues)
      },
      title: t('have_saved_data'),
      onOk: () => setIsReady(true)
    })

    return () => modal.destroy()
  }, [form, t, formattedInitValues, order?.id])

  useEffect(() => {
    if (!financialRange) return

    form.setFieldValue(coefficientMinKey.name, financialRange.min)
    form.setFieldValue(coefficientMaxKey.name, financialRange.max)
  }, [financialRange, form])

  const handleReadyToSaveData = () => setIsReady(true)

  return (
    <FinancialInsuranceProvider
      isEditable={false}
      order={order}
      isCalculatingLoading={isCalculatingLoading || isUpdateOrderLoading}
      handleCalculate={handleCalculate}
      handleRecalculate={handleRecalculate}
      handleUpdateOrder={runStep}
    >
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleReadyToSaveData}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={18}>
            <Form.Item shouldUpdate noStyle>
              {() => <Card>{forms[form.getFieldValue('step')]}</Card>}
            </Form.Item>
          </Col>

          <Col xs={24} lg={6}>
            <Card>
              <Details />
            </Card>
          </Col>
        </Row>

        <Divider />

        <FinancialInsuranceControls
          steps={forms.length}
          onReadyToSaveData={handleReadyToSaveData}
        />

        {isReady && (
          <Form.Item shouldUpdate noStyle>
            {saveOnChangeValue}
          </Form.Item>
        )}
      </Form>
    </FinancialInsuranceProvider>
  )
}
